import React, { useState, useEffect, useCallback } from "react";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Alert,
  Spinner,
  Button,
  Form,
  Modal,
  Table,
  Badge,
  Pagination
} from "react-bootstrap";
import Layout from "../components/Layout";
import { useAuth } from "../hooks/useAuth";
import { useRolePermissions } from "../hooks/useRolePermissions";
import { vacationAPI, childAPI } from "../utils/api";
import type { 
  VacationWithChild, 
  CreateVacationRequest,
  UpdateVacationRequest,
  NurseryVacationFilters 
} from "../types/vacation";
import type { Child } from "../types/child";
import { formatDateInNurseryTimezone, getTodayDateInNurseryTimezone } from "../utils/timezone";
import { useTranslation } from "react-i18next";
import ScopeIndicator from '../components/ScopeIndicator';

const PlusIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="me-1">
    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16" className="text-primary" style={{ cursor: 'pointer' }}>
    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16" className="text-danger" style={{ cursor: 'pointer' }}>
    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
  </svg>
);

const Vacations: React.FC = () => {
  const { currentNursery, nurseriesLoading, userRolePermissionsLoading } = useAuth();
  const { canView, canAdd, canEdit, canDelete } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'vacations' });
  const { t: tc } = useTranslation('common');
  
  // Get nursery timezone with fallback
  const nurseryTimezone = currentNursery?.timezone || 'UTC';
  
  // State management
  const [vacations, setVacations] = useState<VacationWithChild[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 50,
    total: 0
  });
  
  // Pagination states (matching children page)
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Filter states
  const [filters, setFilters] = useState<NurseryVacationFilters>({
    from_date: getTodayDateInNurseryTimezone(nurseryTimezone),
    to_date: getTodayDateInNurseryTimezone(nurseryTimezone),
    per_page: 50,
    page: 1
  });
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);
  const [editingVacation, setEditingVacation] = useState<VacationWithChild | null>(null);
  const [deletingVacation, setDeletingVacation] = useState<VacationWithChild | null>(null);
  const [newVacation, setNewVacation] = useState<CreateVacationRequest>({
    from_date: '',
    to_date: ''
  });
  const [editVacation, setEditVacation] = useState<UpdateVacationRequest>({
    from_date: '',
    to_date: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  // Initialize dates when nursery timezone changes
  useEffect(() => {
    if (currentNursery) {
      const today = getTodayDateInNurseryTimezone(currentNursery.timezone || 'UTC');
      setFilters({
        from_date: today,
        to_date: today,
        per_page: 50,
        page: 1
      });
      setPage(1);
    }
  }, [currentNursery]);

  // Load children for dropdown
  const loadChildren = useCallback(async () => {
    if (!currentNursery?.nursery_id) return;

    try {
      const response = await childAPI.getChildren(currentNursery.nursery_id);
      setChildren(response.children || []);
    } catch (err: any) {
      console.error('Failed to load children:', err);
    }
  }, [currentNursery?.nursery_id]);

  // Load vacations data
  const loadVacations = useCallback(async () => {
    if (!currentNursery?.nursery_id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await vacationAPI.getNurseryVacations(currentNursery.nursery_id, {
        ...filters,
        include_child: 1, // Always include child data
        per_page: rowsPerPage,
        page: page
      });
      setVacations(response.data.items);
      
      // Calculate total from last_page and per_page
      // If we have a total from API, use it; otherwise calculate from pagination data
      let actualTotal = response.data.total;
      
      if (!actualTotal) {
        // Calculate total from last_page and per_page
        const estimatedTotal = response.data.last_page * response.data.per_page;
        
        // If we're on the last page, calculate actual total from items
        if (response.data.current_page === response.data.last_page) {
          actualTotal = (response.data.current_page - 1) * response.data.per_page + response.data.items.length;
        } else {
          actualTotal = estimatedTotal;
        }
      }
      
      setPagination({
        current_page: response.data.current_page,
        per_page: response.data.per_page,
        total: actualTotal
      });
    } catch (err: any) {
      console.error('Failed to load vacations:', err);
      setError(err.response?.data?.message || t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery?.nursery_id, filters, rowsPerPage, page, t]);

  // Load data when component mounts or filters change
  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  useEffect(() => {
    loadVacations();
  }, [loadVacations]);

  // Handle filter changes
  const handleFilterChange = (field: keyof NurseryVacationFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  // Pagination handlers (matching children page)
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to first page when changing items per page
  }, []);

  // Calculate pagination
  const totalPages = Math.ceil(pagination.total / rowsPerPage);

  // Generate pagination items (matching children page)
  const renderPaginationItems = useCallback(() => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages, handlePageChange]);

  // Handle add vacation modal
  const handleShowAddModal = () => {
    setSelectedChild(null);
    setNewVacation({
      from_date: getTodayDateInNurseryTimezone(nurseryTimezone),
      to_date: getTodayDateInNurseryTimezone(nurseryTimezone)
    });
    setModalError(null);
    setModalSuccess(null);
    setError(null); // Clear any main page errors
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setSelectedChild(null);
    setNewVacation({
      from_date: '',
      to_date: ''
    });
    setModalError(null);
    setModalSuccess(null);
  };

  // Handle vacation submission
  const handleSubmitVacation = async () => {
    if (!selectedChild) {
      setModalError(t('selectChildError'));
      return;
    }

    setSubmitting(true);
    setModalError(null);
    setModalSuccess(null);
    
    try {
      const response = await vacationAPI.createChildVacation(selectedChild.id, newVacation);
      setModalSuccess(response.message || t('createSuccess'));
      
      // Reset to first page and refresh the list
      setPage(1);
      // Small delay to ensure cache is cleared
      setTimeout(async () => {
        await loadVacations();
      }, 100);
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseAddModal();
      }, 2000);
      
    } catch (err: any) {
      console.error('Failed to create vacation:', err);
      setModalError(err.response?.data?.message || t('saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit vacation
  const handleEditVacation = (vacation: VacationWithChild) => {
    setEditingVacation(vacation);
    setEditVacation({
      from_date: vacation.from_date,
      to_date: vacation.to_date
    });
    setShowEditModal(true);
    setModalError(null);
    setModalSuccess(null);
  };

  // Handle delete vacation
  const handleDeleteVacation = (vacation: VacationWithChild) => {
    setDeletingVacation(vacation);
    setShowDeleteModal(true);
  };

  // Handle edit vacation submission
  const handleEditSubmit = async () => {
    if (!editingVacation) return;

    setSubmitting(true);
    setModalError(null);

    try {
      await vacationAPI.updateChildVacation(
        editingVacation.child_id,
        editingVacation.id,
        editVacation
      );

      setModalSuccess(t('updateSuccess'));
      await loadVacations();
      
      setTimeout(() => {
        setShowEditModal(false);
        setEditingVacation(null);
        setModalSuccess(null);
      }, 1500);
    } catch (error: any) {
      console.error('Error updating vacation:', error);
      setModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete vacation confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingVacation) return;

    setSubmitting(true);
    setModalError(null);

    try {
      await vacationAPI.deleteChildVacation(
        deletingVacation.child_id,
        deletingVacation.id
      );

      setModalSuccess(t('deleteSuccess'));
      await loadVacations();
      
      setTimeout(() => {
        setShowDeleteModal(false);
        setDeletingVacation(null);
        setModalSuccess(null);
      }, 1500);
    } catch (error: any) {
      console.error('Error deleting vacation:', error);
      setModalError(error.response?.data?.message || t('deleteFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle close edit modal
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingVacation(null);
    setEditVacation({ from_date: '', to_date: '' });
    setModalError(null);
    setModalSuccess(null);
  };

  // Handle close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingVacation(null);
    setModalError(null);
    setModalSuccess(null);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!currentNursery?.timezone) return tc('messages.na');
    return formatDateInNurseryTimezone(dateString, currentNursery.timezone);
  };

  // Calculate vacation duration
  const calculateDuration = (fromDate: string, toDate: string) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const diffTime = Math.abs(to.getTime() - from.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Check if vacation is active today
  const isActiveToday = (fromDate: string, toDate: string) => {
    const today = getTodayDateInNurseryTimezone(nurseryTimezone);
    return today >= fromDate && today <= toDate;
  };

  if (nurseriesLoading || userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
            <p className="text-muted">{t('loading')}</p>
          </div>
        </Container>
      </Layout>
    );
  }

  // Check if user has permission to view vacations
  if (!canView('childrenVacation')) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="px-3">
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>{t('accessDeniedTitle')}</Alert.Heading>
            <p className="mb-0">{t('accessDeniedBody')}</p>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      {/* Header and Controls Container */}
      <Container fluid className="px-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
          <h2 className="fw-bold text-primary mb-3 mb-md-0">
            {t('title')}
          </h2>
          {currentNursery && canAdd('childrenVacation') && (
            <Button variant="primary" onClick={handleShowAddModal}>
              <PlusIcon />
              {t('addVacation')}
            </Button>
          )}
        </div>

        {currentNursery && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentNursery.description} (ID: {currentNursery.nursery_id})
          </Alert>
        )}

        <ScopeIndicator resource="childrenVacation" />

        {!currentNursery && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectNursery', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {/* Filters Section */}
        {currentNursery && (
          <Card className="mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{t('filtersTitle')}</h5>
                <small className="text-muted">
                  {t('nurseryNote', { name: currentNursery.description })}
                </small>
              </div>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={(e) => { e.preventDefault(); }}>
                <Row>
                  <Col lg={3} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{tc('labels.fromDate')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.from_date}
                        onChange={(e) => handleFilterChange('from_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col lg={3} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{tc('labels.toDate')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.to_date}
                        onChange={(e) => handleFilterChange('to_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col lg={3} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{tc('labels.child')}</Form.Label>
                      <Form.Select
                        value={filters.child_id || ''}
                        onChange={(e) => handleFilterChange('child_id', e.target.value ? parseInt(e.target.value) : undefined)}
                      >
                        <option value="">{tc('labels.allChildren')}</option>
                        {children.map(child => (
                          <option key={child.id} value={child.id}>
                            {child.name} ({child.classroom?.name})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Button type="button" variant="outline-secondary" onClick={() => {
                        setFilters({
                          from_date: getTodayDateInNurseryTimezone(nurseryTimezone),
                          to_date: getTodayDateInNurseryTimezone(nurseryTimezone),
                          per_page: 50,
                          page: 1
                        });
                      }}>
                        {tc('buttons.clearFilters')}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        )}
      </Container>

      {/* Table Content Container */}
      <Container fluid className="px-3">

          {/* Loading State */}
          {loading && (
            <Row className="justify-content-center mb-4">
              <Col xs={12} className="text-center">
                <div className="py-5">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <h5 className="text-muted">{t('loadingData')}</h5>
                </div>
              </Col>
            </Row>
          )}

          {/* Error State */}
          {error && (
            <Row className="mb-4">
              <Col xs={12}>
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                  <Alert.Heading>{t('errorTitle')}</Alert.Heading>
                  <p className="mb-0">{error}</p>
                </Alert>
              </Col>
            </Row>
          )}

          {/* Vacations Content */}
          {!loading && currentNursery && (
            <>
              {/* Vacations Table */}
              <Card className="mb-4">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      {t('records', { count: pagination.total })}
                    </h5>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">

                  {vacations.length === 0 ? (
                    <div className="text-center py-4 py-md-5">
                      <div style={{ fontSize: '3rem', opacity: '0.3', marginBottom: '1rem' }}>🏖️</div>
                      <h5 className="mb-2 fw-bold text-muted">{t('noTitle')}</h5>
                      <p className="text-muted mb-0 small">
                        {t('noRecords')}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Desktop Table View */}
                      <div className="d-none d-md-block">
                        <div className="table-responsive">
                          <Table hover className="mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>{tc('labels.child')}</th>
                                <th>{tc('labels.fromDate')}</th>
                                <th>{tc('labels.toDate')}</th>
                                <th>{t('duration')}</th>
                                <th>{tc('labels.status')}</th>
                                <th>{tc('labels.created')}</th>
                                <th>{tc('labels.actions')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {vacations.map((vacation) => (
                                <tr key={vacation.id}>
                                  <td>
                                    <div>
                                      <div className="fw-medium">{vacation.child?.name || t('unknown')}</div>
                                      <small className="text-muted">{t('childIdLabel', { id: vacation.child_id })}</small>
                                    </div>
                                  </td>
                                  <td>
                                    <Badge bg="light" text="dark">
                                      {formatDate(vacation.from_date)}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge bg="light" text="dark">
                                      {formatDate(vacation.to_date)}
                                    </Badge>
                                  </td>
                                  <td>
                                    <span className="fw-medium text-primary">
                                      {t('days', { count: calculateDuration(vacation.from_date, vacation.to_date) })}
                                    </span>
                                  </td>
                                  <td>
                                    {isActiveToday(vacation.from_date, vacation.to_date) ? (
                                      <Badge bg="success">
                                        <i className="bi bi-check-circle me-1"></i>
                                        {tc('status.active')}
                                      </Badge>
                                    ) : new Date(vacation.from_date) > new Date() ? (
                                      <Badge bg="info">
                                        <i className="bi bi-clock me-1"></i>
                                        {t('upcoming')}
                                      </Badge>
                                    ) : (
                                      <Badge bg="secondary">
                                        <i className="bi bi-schedule-check me-1"></i>
                                        {t('completed')}
                                      </Badge>
                                    )}
                                  </td>
                                  <td>
                                    <small className="text-muted">
                                      {formatDate(vacation.created_at)}
                                    </small>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      {canEdit('childrenVacation') && (
                                        <span
                                          onClick={() => handleEditVacation(vacation)}
                                          style={{ cursor: 'pointer' }}
                                          title={t('editTooltip')}
                                        >
                                          <EditIcon />
                                        </span>
                                      )}
                                      {canDelete('childrenVacation') && (
                                        <span
                                          onClick={() => handleDeleteVacation(vacation)}
                                          style={{ cursor: 'pointer' }}
                                          title={t('deleteTooltip')}
                                        >
                                          <DeleteIcon />
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>

                      {/* Mobile Card View */}
                      <div className="d-md-none">
                        {vacations.map((vacation) => (
                          <Card key={vacation.id} className="mb-3 border-0 shadow-sm">
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="flex-grow-1">
                                  <h6 className="mb-1 fw-bold">{vacation.child?.name || t('unknown')}</h6>
                                  <small className="text-muted">{t('childIdLabel', { id: vacation.child_id })}</small>
                                </div>
                                {isActiveToday(vacation.from_date, vacation.to_date) ? (
                                  <Badge bg="success" className="px-2 py-1">
                                    <i className="bi bi-check-circle me-1"></i>
                                    {tc('status.active')}
                                  </Badge>
                                ) : new Date(vacation.from_date) > new Date() ? (
                                  <Badge bg="info" className="px-2 py-1">
                                    <i className="bi bi-clock me-1"></i>
                                    {t('upcoming')}
                                  </Badge>
                                ) : (
                                  <Badge bg="secondary" className="px-2 py-1">
                                    <i className="bi bi-schedule-check me-1"></i>
                                    {t('completed')}
                                  </Badge>
                                )}
                              </div>
                              <Row className="g-2">
                                <Col xs={6}>
                                  <small className="text-muted d-block">{tc('labels.fromDate')}</small>
                                  <Badge bg="light" text="dark" className="px-2 py-1">
                                    {formatDate(vacation.from_date)}
                                  </Badge>
                                </Col>
                                <Col xs={6}>
                                  <small className="text-muted d-block">{tc('labels.toDate')}</small>
                                  <Badge bg="light" text="dark" className="px-2 py-1">
                                    {formatDate(vacation.to_date)}
                                  </Badge>
                                </Col>
                                <Col xs={6}>
                                  <small className="text-muted d-block">{t('duration')}</small>
                                  <span className="fw-medium text-primary">
                                    {t('days', { count: calculateDuration(vacation.from_date, vacation.to_date) })}
                                  </span>
                                </Col>
                                <Col xs={6}>
                                  <small className="text-muted d-block">{tc('labels.created')}</small>
                                  <small className="text-muted">
                                    {formatDate(vacation.created_at)}
                                  </small>
                                </Col>
                              </Row>
                              {(canEdit('childrenVacation') || canDelete('childrenVacation')) && (
                                <div className="d-flex gap-2 mt-3 pt-2 border-top">
                                  {canEdit('childrenVacation') && (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleEditVacation(vacation)}
                                      className="d-flex align-items-center"
                                    >
                                      <EditIcon />
                                      <span className="ms-1">{tc('buttons.edit')}</span>
                                    </Button>
                                  )}
                                  {canDelete('childrenVacation') && (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDeleteVacation(vacation)}
                                      className="d-flex align-items-center"
                                    >
                                      <DeleteIcon />
                                      <span className="ms-1">{tc('buttons.delete')}</span>
                                    </Button>
                                  )}
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    </>
                  )}
                </Card.Body>
                
                {/* Pagination */}
                {pagination.total > rowsPerPage && (
                  <Card.Footer>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">
                        {tc('messages.showing', { from: ((page - 1) * rowsPerPage) + 1, to: Math.min(page * rowsPerPage, pagination.total), total: pagination.total, entity: t('title').toLowerCase() })}
                      </span>
                      <Pagination className="mb-0">
                        <Pagination.Prev 
                          disabled={page === 1}
                          onClick={() => handlePageChange(page - 1)}
                        />
                        {renderPaginationItems()}
                        <Pagination.Next 
                          disabled={page === totalPages}
                          onClick={() => handlePageChange(page + 1)}
                        />
                      </Pagination>
                    </div>
                  </Card.Footer>
                )}
              </Card>

              {/* Items per page selector (matching children page) */}
              <Row className="mt-3 mb-4">
                <Col className="d-flex justify-content-center align-items-center gap-2">
                  <span className="text-muted small">{tc('messages.itemsPerPage')}</span>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={rowsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </Col>
              </Row>
            </>
          )}

          {/* No Data State */}
          {!loading && !currentNursery && !error && (
            <Row className="mb-4">
              <Col xs={12}>
                <Card className="text-center">
                  <Card.Body className="py-5">
                    <div style={{ fontSize: '4rem', opacity: '0.3', marginBottom: '1rem' }}>🏖️</div>
                    <h5 className="mb-2 fw-bold text-muted">{t('noDataTitle')}</h5>
                    <p className="text-muted mb-0">
                      {t('noDataBody')}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
      </Container>

      {/* Add Vacation Modal */}
        <Modal show={showAddModal} onHide={handleCloseAddModal} size="lg" centered>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={(e) => { e.preventDefault(); handleSubmitVacation(); }}>
            <Modal.Body className="py-2 py-md-3">
            {modalSuccess && (
              <Alert variant="success" className="mb-3">
                <i className="bi bi-check-circle me-2"></i>
                {modalSuccess}
              </Alert>
            )}
            
            {modalError && (
              <Alert variant="danger" className="mb-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {modalError}
              </Alert>
            )}
            
              <Row className="g-2 g-md-3">
                <Col xs={12} className="mb-2 mb-md-3">
                  <Form.Group>
                    <Form.Label className="small fw-medium">{t('selectChild')}</Form.Label>
                    <Form.Select
                      value={selectedChild?.id || ''}
                      onChange={(e) => {
                        const childId = parseInt(e.target.value);
                        const child = children.find(candidate => candidate.id === childId);
                        setSelectedChild(child || null);
                      }}
                      required
                      size="sm"
                    >
                      <option value="">{t('chooseChild')}</option>
                      {children.map(child => (
                        <option key={child.id} value={child.id}>
                          {child.name} ({child.classroom?.name})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-2 g-md-3">
                <Col xs={12} sm={6} className="mb-2 mb-md-3">
                  <Form.Group>
                    <Form.Label className="small fw-medium">{tc('labels.fromDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={newVacation.from_date}
                      onChange={(e) => setNewVacation(prev => ({ ...prev, from_date: e.target.value }))}
                      required
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} className="mb-2 mb-md-3">
                  <Form.Group>
                    <Form.Label className="small fw-medium">{tc('labels.toDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={newVacation.to_date}
                      onChange={(e) => setNewVacation(prev => ({ ...prev, to_date: e.target.value }))}
                      required
                      size="sm"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {selectedChild && (
                <Row className="g-2 g-md-3">
                  <Col xs={12} className="mb-2 mb-md-3">
                    <Alert variant="info" className="py-2 py-md-3">
                      <div className="d-flex align-items-center">
                        <div style={{
                          width: '35px',
                          height: '35px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '0.75rem',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          flexShrink: 0
                        }}>
                          {selectedChild.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-bold small">{selectedChild.name}</div>
                          <small className="text-muted">{t('childIdLabel', { id: selectedChild.id })}</small>
                        </div>
                      </div>
                    </Alert>
                  </Col>
                </Row>
              )}

              {newVacation.from_date && newVacation.to_date && (
                <Row className="g-2 g-md-3">
                  <Col xs={12} className="mb-2 mb-md-3">
                    <Alert variant="success" className="py-2 py-md-3">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-schedule-check me-2" style={{ fontSize: '1rem', flexShrink: 0 }}></i>
                        <div className="flex-grow-1">
                          <div className="fw-bold small">{t('vacationDuration')}</div>
                          <div className="text-success small">
                            {t('days', { count: calculateDuration(newVacation.from_date, newVacation.to_date) })}
                            {newVacation.from_date === newVacation.to_date && ` ${t('singleDay')}`}
                          </div>
                        </div>
                      </div>
                    </Alert>
                  </Col>
                </Row>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseAddModal} disabled={submitting}>
                {tc('buttons.cancel')}
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={submitting || !selectedChild || !newVacation.from_date || !newVacation.to_date}
              >
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t('adding')}
                  </>
                ) : (
                  <>
                    <PlusIcon />
                    {t('addVacation')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Edit Vacation Modal */}
        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg" centered>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('editTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={(e) => { e.preventDefault(); handleEditSubmit(); }}>
            <Modal.Body className="py-2 py-md-3">
              {modalError && (
                <Alert variant="danger" className="mb-3">
                  {modalError}
                </Alert>
              )}
              {modalSuccess && (
                <Alert variant="success" className="mb-3">
                  {modalSuccess}
                </Alert>
              )}
              
              {editingVacation && (
                <div className="mb-3">
                  <h6 className="text-muted mb-2">{tc('labels.child')}</h6>
                  <div className="p-2 bg-light rounded">
                    <strong>{editingVacation.child?.name || t('unknown')}</strong>
                    <br />
                    <small className="text-muted">{t('childIdLabel', { id: editingVacation.child_id })}</small>
                  </div>
                </div>
              )}

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('fromDateRequired')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={editVacation.from_date}
                      onChange={(e) => setEditVacation({ ...editVacation, from_date: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('toDateRequired')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={editVacation.to_date}
                      onChange={(e) => setEditVacation({ ...editVacation, to_date: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="py-2 py-md-3">
              <Button variant="secondary" onClick={handleCloseEditModal} disabled={submitting}>
                {tc('buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {tc('loading.updating')}
                  </>
                ) : (
                  <>
                    <EditIcon />
                    {t('updateVacation')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Vacation Modal */}
        <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('deleteTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-2 py-md-3">
            {modalError && (
              <Alert variant="danger" className="mb-3">
                {modalError}
              </Alert>
            )}
            {modalSuccess && (
              <Alert variant="success" className="mb-3">
                {modalSuccess}
              </Alert>
            )}
            
            {deletingVacation && (
              <div>
                <p className="mb-3">
                  {t('deleteConfirmFor', { name: deletingVacation.child?.name || t('unknown') })}
                </p>
                <div className="p-3 bg-light rounded">
                  <Row>
                    <Col xs={6}>
                      <small className="text-muted d-block">{tc('labels.fromDate')}</small>
                      <strong>{formatDate(deletingVacation.from_date)}</strong>
                    </Col>
                    <Col xs={6}>
                      <small className="text-muted d-block">{tc('labels.toDate')}</small>
                      <strong>{formatDate(deletingVacation.to_date)}</strong>
                    </Col>
                    <Col xs={12} className="mt-2">
                      <small className="text-muted d-block">{t('duration')}</small>
                      <strong>{t('days', { count: calculateDuration(deletingVacation.from_date, deletingVacation.to_date) })}</strong>
                    </Col>
                  </Row>
                </div>
                <p className="text-danger mt-3 mb-0">
                  <small>{tc('messages.cannotBeUndone')}</small>
                </p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="py-2 py-md-3">
            <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={submitting}>
              {tc('buttons.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {tc('loading.deleting')}
                </>
              ) : (
                <>
                  <DeleteIcon />
                  {t('deleteVacation')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
    </Layout>
  );
};

export default Vacations;
